/**
 * Logistics Database (L2) - Customer Master
 * Version: 1.1.0
 * Description:
 *   Master data management for delivery customers.
 *   Features Excel import/export with conflict detection and resolution.
 *   Includes role-based Edit/Delete operations (Admin/Manager only).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  Tooltip,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  CloudUpload as ImportIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CompareArrows as ConflictIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { logisticsAPI } from '../../api';
import useAuthStore from '../../store/authStore';
import dayjs from 'dayjs';

// --- Types ---
interface Customer {
  id: string;
  unique_id: string;
  brand_name: string;
  billing_name: string;
  zone_area: string;
  vehicle_no: string;
  priority: number;
  gps_coordinates: string;
  delivery_address: string;
  phone: string;
}

interface ConflictDetail {
  field: string;
  db_value: any;
  excel_value: any;
}

interface Conflict {
  unique_id: string;
  conflicts: ConflictDetail[];
  excel_row_data: any;
}

interface Depot {
  id: string;
  name: string;
  location: string;
  address?: string;
  is_active: boolean;
  is_default: boolean;
}

// --- Custom Tab Panel ---
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// --- Component ---
export default function LogisticsDatabase() {
  const { enqueueSnackbar } = useSnackbar();
  const user = useAuthStore((state) => state.user);
  const isAuthorized = user?.role === 'Admin' || user?.role === 'Manager';
  
  const [tabValue, setTabValue] = useState(0);

  // Customers State
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [search, setSearch] = useState('');
  
  // Depots State
  const [depots, setDepots] = useState<Depot[]>([]);
  const [depotsLoading, setDepotsLoading] = useState(false);
  
  // Metadata
  const [dbMetadata, setDbMetadata] = useState<{ last_sync_at: string, file_name: string } | null>(null);
  
  // UI State
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedDepot, setSelectedDepot] = useState<Depot | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Depot Form State
  const [depotFormOpen, setDepotFormOpen] = useState(false);
  const [depotData, setDepotData] = useState<Partial<Depot>>({
    name: '',
    location: '',
    address: '',
    is_active: true,
    is_default: false
  });
  
  // Import State
  const [importLoading, setImportLoading] = useState(false);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);

  // Form State
  const [formData, setFormData] = useState<Partial<Customer>>({
    unique_id: '',
    brand_name: '',
    billing_name: '',
    zone_area: '',
    vehicle_no: '',
    priority: 1,
    gps_coordinates: '',
    delivery_address: '',
    phone: ''
  });

  // Fetch Data
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await logisticsAPI.getCustomers({
        skip: paginationModel.page * paginationModel.pageSize,
        limit: paginationModel.pageSize,
        search: search || undefined
      });
      setCustomers(data.customers || []);
      setTotal(data.total || 0);
    } catch (error) {
      enqueueSnackbar('Failed to fetch customers', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [paginationModel, search, enqueueSnackbar]);

  const fetchDepots = useCallback(async () => {
    setDepotsLoading(true);
    try {
      const data = await logisticsAPI.listDepots();
      setDepots(data || []);
    } catch (error) {
      enqueueSnackbar('Failed to fetch depots', { variant: 'error' });
    } finally {
      setDepotsLoading(false);
    }
  }, [enqueueSnackbar]);

  const fetchMetadata = useCallback(async () => {
    try {
      const data = await logisticsAPI.getDBMetadata();
      setDbMetadata(data);
    } catch (error) {
      console.error('Failed to fetch DB metadata', error);
    }
  }, []);

  useEffect(() => {
    if (tabValue === 0) {
      fetchCustomers();
      fetchMetadata();
    }
    if (tabValue === 1) fetchDepots();
  }, [tabValue, fetchCustomers, fetchDepots, fetchMetadata]);

  // Handlers
  const handleOpenForm = (customer: Customer | null = null) => {
    if (customer) {
      setSelectedCustomer(customer);
      setFormData(customer);
    } else {
      setSelectedCustomer(null);
      setFormData({
        unique_id: '',
        brand_name: '',
        billing_name: '',
        zone_area: '',
        vehicle_no: '',
        priority: 1,
        gps_coordinates: '',
        delivery_address: '',
        phone: ''
      });
    }
    setFormDialogOpen(true);
  };

  const handleCloseForm = () => {
    setFormDialogOpen(false);
    setSelectedCustomer(null);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (selectedCustomer) {
        await logisticsAPI.updateCustomer(selectedCustomer.id, formData);
        enqueueSnackbar('Customer updated successfully', { variant: 'success' });
      } else {
        await logisticsAPI.createCustomer(formData);
        enqueueSnackbar('Customer created successfully', { variant: 'success' });
      }
      handleCloseForm();
      fetchCustomers();
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Failed to save customer';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConflictResolve = (uniqueId: string) => {
    setConflicts(prev => prev.filter(c => c.unique_id !== uniqueId));
    if (conflicts.length === 1) {
      setConflictDialogOpen(false);
      fetchCustomers();
    }
  };

  // --- Depot Handlers ---
  const handleOpenDepotForm = (depot?: Depot) => {
    if (depot) {
      setSelectedDepot(depot);
      setDepotData({
        name: depot.name,
        location: depot.location,
        address: depot.address || '',
        is_active: depot.is_active,
        is_default: depot.is_default
      });
    } else {
      setSelectedDepot(null);
      setDepotData({
        name: '',
        location: '',
        address: '',
        is_active: true,
        is_default: false
      });
    }
    setDepotFormOpen(true);
  };

  const handleDepotSubmit = async () => {
    setSubmitting(true);
    try {
      if (selectedDepot) {
        await logisticsAPI.updateDepot(selectedDepot.id, depotData);
        enqueueSnackbar('Depot updated successfully', { variant: 'success' });
      } else {
        await logisticsAPI.createDepot(depotData);
        enqueueSnackbar('Depot created successfully', { variant: 'success' });
      }
      setDepotFormOpen(false);
      fetchDepots();
    } catch (error: any) {
      enqueueSnackbar('Failed to save depot', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDepot = async (id: string) => {
     if (!window.confirm('Are you sure you want to delete this depot? This will archive it.')) return;
     try {
        await logisticsAPI.deleteDepot(id);
        enqueueSnackbar('Depot archived successfully', { variant: 'success' });
        fetchDepots();
     } catch (error) {
        enqueueSnackbar('Failed to delete depot', { variant: 'error' });
     }
  };

  const handleDeleteClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedCustomer) return;
    setSubmitting(true);
    try {
      await logisticsAPI.deleteCustomer(selectedCustomer.id);
      enqueueSnackbar('Customer deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      fetchCustomers();
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Failed to delete customer';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSubmitting(false);
      setSelectedCustomer(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await logisticsAPI.getTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'logistics_customer_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      enqueueSnackbar('Failed to download template', { variant: 'error' });
    }
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    try {
      const result = await logisticsAPI.importCustomers(file);
      
      if (result.conflicts && result.conflicts.length > 0) {
        setConflicts(result.conflicts);
        setConflictDialogOpen(true);
        enqueueSnackbar(`Imported ${result.imported} rows. Found ${result.conflicts.length} conflicts.`, { variant: 'warning' });
      } else {
        enqueueSnackbar(`Successfully imported ${result.imported} new customers!`, { variant: 'success' });
        fetchCustomers();
        fetchMetadata();
      }
      
      if (result.errors && result.errors.length > 0) {
        enqueueSnackbar(`${result.errors.length} rows had errors and were skipped.`, { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Excel import failed', { variant: 'error' });
    } finally {
      setImportLoading(false);
      event.target.value = ''; // Reset input
    }
  };

  // Columns
  const columns: GridColDef[] = [
    { field: 'unique_id', headerName: 'Unique ID', width: 120, 
      renderCell: (params) => <Chip label={params.value} size="small" color="primary" variant="outlined" />
    },
    { field: 'brand_name', headerName: 'Brand Name', width: 220, 
      renderCell: (params) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{params.value}</Typography>
    },
    { field: 'billing_name', headerName: 'Billing Name', width: 200 },
    { field: 'zone_area', headerName: 'Zone/Area', width: 130 },
    { field: 'vehicle_no', headerName: 'Vehicle No', width: 130 },
    { field: 'priority', headerName: 'Priority', width: 90, type: 'number',
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color={params.value <= 2 ? 'error' : params.value <= 5 ? 'warning' : 'default'} 
        />
      )
    },
    { field: 'phone', headerName: 'Phone', width: 150 },
    { field: 'actions', headerName: 'Actions', width: 120, sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title={isAuthorized ? "Edit" : "Permission Denied"}>
            <span>
              <IconButton 
                size="small" 
                color="primary" 
                disabled={!isAuthorized}
                onClick={() => handleOpenForm(params.row)}
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={isAuthorized ? "Delete" : "Permission Denied"}>
            <span>
              <IconButton 
                size="small" 
                color="error" 
                disabled={!isAuthorized}
                onClick={() => handleDeleteClick(params.row)}
              >
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, val) => setTabValue(val)} aria-label="Logistics Database Tabs">
          <Tab label="Customer Master" />
          <Tab label="Depot Management" />
        </Tabs>
      </Box>

      <CustomTabPanel value={tabValue} index={0}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              Logistics Database
            </Typography>
             <Typography variant="body2" color="text.secondary">
              L2 submodule: Manage primary customer records and zone mapping.
            </Typography>
            {dbMetadata && (
              <Stack direction="row" spacing={3} mt={1}>
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  Last Update Date: {dayjs(dbMetadata.last_sync_at).format('DD MMM YYYY, hh:mm A')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 600 }}>
                  File name: {dbMetadata.file_name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                  Total Customers: {dbMetadata.total_customers ?? 0}
                </Typography>
              </Stack>
            )}
          </Box>
          <Stack direction="row" spacing={2}>
            {isAuthorized && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadTemplate}
                >
                  Template
                </Button>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<ImportIcon />}
                  disabled={importLoading}
                >
                  {importLoading ? 'Importing...' : 'Import Excel'}
                  <input
                    type="file"
                    hidden
                    accept=".xlsx, .xls, .csv"
                    onChange={handleImportFile}
                  />
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenForm()}
                >
                  Add Customer
                </Button>
              </>
            )}
          </Stack>
        </Stack>

        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 2 }}>
             <TextField
                fullWidth
                placeholder="Search customers by brand name, unique ID, or route..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
                }}
             />
          </Box>
          <DataGrid
            rows={customers}
            columns={columns}
            loading={loading}
            rowCount={total}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            autoHeight
            disableRowSelectionOnClick
            slots={{ toolbar: GridToolbarContainer }}
            slotProps={{
              toolbar: {
                children: <GridToolbarExport />
              }
            }}
            sx={{ border: 'none' }}
          />
        </Card>
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
           <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                 Depot Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                 Define your starting and ending points for delivery runs.
              </Typography>
           </Box>
           {isAuthorized && (
              <Button
                 variant="contained"
                 startIcon={<AddIcon />}
                 onClick={() => handleOpenDepotForm()}
              >
                 Add Depot
              </Button>
           )}
        </Stack>

        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
           <Table>
              <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                 <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Depot Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Location (Lat, Lng)</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    {isAuthorized && <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>}
                 </TableRow>
              </TableHead>
              <TableBody>
                 {depotsLoading ? (
                    <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                 ) : depots.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center">No depots found.</TableCell></TableRow>
                 ) : depots.map((depot) => (
                    <TableRow key={depot.id} hover>
                       <TableCell sx={{ fontWeight: 500 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                             {depot.name}
                             {depot.is_default && <Chip label="DEFAULT" size="small" color="primary" sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem' }} />}
                          </Stack>
                       </TableCell>
                       <TableCell><Chip label={depot.location} size="small" variant="outlined" color="primary" /></TableCell>
                       <TableCell>{depot.address || 'N/A'}</TableCell>
                       <TableCell>
                          <Chip 
                             label={depot.is_active ? 'Active' : 'Inactive'} 
                             size="small" 
                             color={depot.is_active ? 'success' : 'default'} 
                          />
                       </TableCell>
                       {isAuthorized && (
                          <TableCell align="right">
                             <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Tooltip title="Edit">
                                   <IconButton size="small" onClick={() => handleOpenDepotForm(depot)}>
                                      <EditIcon fontSize="small" />
                                   </IconButton>
                                </Tooltip>
                                <Tooltip title="Archive">
                                   <IconButton size="small" sx={{ color: 'error.main' }} onClick={() => handleDeleteDepot(depot.id)}>
                                      <DeleteIcon fontSize="small" />
                                   </IconButton>
                                </Tooltip>
                             </Stack>
                          </TableCell>
                       )}
                    </TableRow>
                 ))}
              </TableBody>
           </Table>
        </TableContainer>
      </CustomTabPanel>

      {/* --- FORMS & DIALOGS --- */}
      
      {/* Customer Form Dialog */}
      <Dialog open={formDialogOpen} onClose={handleCloseForm} fullWidth maxWidth="md">
        <DialogTitle>{selectedCustomer ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth label="Unique ID" required
                value={formData.unique_id}
                onChange={(e) => setFormData({ ...formData, unique_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth label="Brand Name" required
                value={formData.brand_name}
                onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Billing Name"
                value={formData.billing_name}
                onChange={(e) => setFormData({ ...formData, billing_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Zone Area"
                value={formData.zone_area}
                onChange={(e) => setFormData({ ...formData, zone_area: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Vehicle No (e.g. Veh 1)"
                value={formData.vehicle_no}
                onChange={(e) => setFormData({ ...formData, vehicle_no: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth label="Priority (Stop Order)" type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
                placeholder="e.g. 1, 10, 100"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth label="GPS Coordinates (Lat, Lng)"
                value={formData.gps_coordinates}
                onChange={(e) => setFormData({ ...formData, gps_coordinates: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Delivery Address" multiline rows={2}
                value={formData.delivery_address}
                onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>Cancel</Button>
          <Button variant="contained" disabled={submitting} onClick={handleSubmit}>
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete customer "{selectedCustomer?.brand_name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={submitting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Depot Form Dialog */}
      <Dialog open={depotFormOpen} onClose={() => setDepotFormOpen(false)} fullWidth maxWidth="sm">
         <DialogTitle>{selectedDepot ? 'Edit Depot' : 'Add New Depot'}</DialogTitle>
         <DialogContent dividers>
            <Stack spacing={2}>
               <TextField 
                  fullWidth label="Depot Name" required 
                  placeholder="e.g. Main Chennai Hub"
                  value={depotData.name}
                  onChange={(e) => setDepotData({...depotData, name: e.target.value})}
               />
               <TextField 
                  fullWidth label="Location (Latitude, Longitude)" required 
                  placeholder="13.0827, 80.2707"
                  value={depotData.location}
                  onChange={(e) => setDepotData({...depotData, location: e.target.value})}
               />
               <TextField 
                  fullWidth label="Full Address" multiline rows={3}
                  value={depotData.address}
                  onChange={(e) => setDepotData({...depotData, address: e.target.value})}
               />
               <Stack direction="row" spacing={4}>
                  <FormControlLabel 
                     control={<Switch checked={depotData.is_active} onChange={(e) => setDepotData({...depotData, is_active: e.target.checked})} />}
                     label="Is Active"
                  />
                  <FormControlLabel 
                     control={
                       <Switch 
                         checked={depotData.is_default} 
                         onChange={(e) => setDepotData({...depotData, is_default: e.target.checked})} 
                       />
                     }
                     label="Set as Global Default"
                  />
               </Stack>
            </Stack>
         </DialogContent>
         <DialogActions>
            <Button onClick={() => setDepotFormOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleDepotSubmit} disabled={submitting}>
               {submitting ? 'Saving...' : 'Save Depot'}
            </Button>
         </DialogActions>
      </Dialog>

      {/* Conflict Resolution Dialog */}
      <Dialog 
        open={conflictDialogOpen} 
        onClose={() => setConflictDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ConflictIcon color="warning" />
          Import Conflicts Detected
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            The following Unique IDs already exist in the database but have different values in your Excel sheet. 
            <strong> Please manual review and update them if needed.</strong> 
          </Typography>
          
          <Stack spacing={3} mt={2}>
            {conflicts.map((conflict, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2, backgroundColor: 'rgba(255, 152, 0, 0.05)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Customer: {conflict.unique_id}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Existing (DB)</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>New (Excel)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {conflict.conflicts.map((c, cIdx) => (
                        <TableRow key={cIdx}>
                          <TableCell>{c.field}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{c.db_value || '-'}</TableCell>
                          <TableCell sx={{ color: 'error.main', fontWeight: 600 }}>{c.excel_value || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConflictDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => setConflictDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
